import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  Lock,
  Loader2,
  XCircle
} from "lucide-react";
import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { statusMeta, type PillStatus, type StatusGlyph } from "../lib/status";

/**
 * SHARED PRIMITIVES.
 *
 * This module and its CSS layer are the ONLY place permitted to define a card, a button, a
 * pill or a spacing value. A screen may COMPOSE these; it may never re-improvise one, and it
 * may never restyle a primitive's internals — across cascade layers specificity is IGNORED,
 * so a screen rule beats a primitives rule unconditionally and silently repaints it
 * everywhere. If a screen needs something a primitive cannot do, THE PRIMITIVE CHANGES.
 * That rule is what stops the inconsistency coming back.
 *
 * Authored PHONE FIRST. Base = 390px. Complexity is only ever ADDED upward.
 *
 * The honesty primitives live in their own modules because they carry the product's promise:
 *   components/GateMeter.tsx     the QA instrument that cannot fabricate a number
 *   components/DocumentDiff.tsx  the one before/after, word-marked, never truncated
 *   components/OptionCard.tsx    a governed choice that cannot ship without its consequence
 *   components/Sheet.tsx         the modal contract
 */

/* ============================================================================ PANEL
   THE one card. HARD RULE: A PANEL MAY NOT CONTAIN A PANEL. Nested sections are
   separated by a hairline and whitespace, never by another bordered box. */

export function Panel({
  children,
  className,
  padded,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  /** Apply --panel-pad directly to the panel. Omit when the panel hosts its own rows. */
  padded?: boolean;
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <section {...rest} className={`panel ${padded ? "panel-padded" : ""} ${className || ""}`}>
      {children}
    </section>
  );
}

/**
 * PANEL HEADER.
 *
 * The default is a PLAIN HEADING. No gradient, no 64px min-height, no icon in a rounded
 * tile, no two-line title where one will do.
 *
 * `icon` is permitted ONLY when the panel maps 1:1 to a nav destination, so that it reads as
 * a wayfinding echo rather than as decoration. `meta` is permitted ONLY when it carries
 * information the title does not — a count, a live state, a timestamp. "Prompt editor /
 * Draft changes" and "Settings / Runtime" were filler and are gone.
 */
export function PanelHeader({
  title,
  meta,
  icon,
  actions,
  level = 2
}: {
  title: string;
  meta?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  level?: 2 | 3;
}) {
  const Heading = level === 3 ? "h3" : "h2";
  return (
    <div className="panel-header">
      {icon ? (
        <span className="panel-header-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="panel-header-copy">
        <Heading>{title}</Heading>
        {meta ? <p className="panel-header-meta">{meta}</p> : null}
      </div>
      {actions ? <div className="panel-header-actions">{actions}</div> : null}
    </div>
  );
}

/** A hairline section break INSIDE a panel. This is what replaces a nested card. */
export function PanelSection({
  title,
  children,
  className
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel-section ${className || ""}`}>
      {title ? <h3 className="panel-section-title">{title}</h3> : null}
      {children}
    </section>
  );
}

/* ============================================================================ BUTTON
   variant x size, orthogonal. States are defined ONCE, here: hover raises the elevation,
   :active presses, :focus-visible always shows the one ring, :disabled dims — and a
   disabled button must be given a plain-language reason NEXT TO IT, never 1,200px up the
   page. `reason` renders that adjacency structurally. */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export function Button({
  variant = "secondary",
  size = "md",
  block,
  busy,
  reason,
  children,
  className,
  ...rest
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full width. Primary actions are block on phone by default via CSS. */
  block?: boolean;
  busy?: boolean;
  /** Shown adjacent to the button whenever it is disabled. Plain language. */
  reason?: string;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const button = (
    <button
      type="button"
      {...rest}
      className={`btn btn-${variant} btn-${size} ${block ? "btn-block" : ""} ${className || ""}`}
      aria-busy={busy ? "true" : undefined}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
  if (!reason || !rest.disabled) return button;
  return (
    <span className="btn-with-reason">
      {button}
      <span className="btn-reason">{reason}</span>
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" role={label ? "status" : undefined} aria-live={label ? "polite" : undefined}>
      <Loader2 aria-hidden="true" />
      {label ? <span className="spinner-label">{label}</span> : null}
    </span>
  );
}

/* ============================================================================ NAV
   ONE destination component, THREE contexts, and it never changes shape between them: the
   phone's bottom tab bar (glyph over label), the header's inline row at >=768 (label only),
   and the More sheet (glyph beside label, full-width row). Layout is the container's job —
   see the chrome layer — so a destination cannot end up with a different tap target, a
   different active treatment or a different label depending on where it happens to be
   rendered. It was `.side-link`, which is a class named after a sidebar this app has never
   had at any viewport.

   The icon takes NO size prop: --icon-md sizes it from CSS, so every glyph in the chrome is
   the same size and is optically centred in a fixed box rather than left to the SVG's own
   bounding box. */

/**
 * `description` is the one line that says WHAT THIS DESTINATION SHOWS, in plain English.
 * It is not decoration: the owner of this product could not say what his own tabs pointed
 * at, so every destination now carries its answer with it. The description is PAINTED in
 * the menu sheet (which is the whole nav on a phone) and is announced by `onHint` to the
 * header's caption line as the pointer or the keyboard crosses the row at >=768. It is
 * never a tooltip that only a mouse can find.
 */
export function NavItem({
  to,
  icon,
  label,
  description,
  end,
  onHint
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  description?: string;
  end?: boolean;
  onHint?: (hint: { label: string; description: string } | null) => void;
}) {
  const hint = description ? { label, description } : null;
  return (
    <NavLink
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      to={to}
      end={end}
      data-testid="nav-link"
      data-nav={label}
      // The description was shown in the header caption strip, now retired on desktop. It
      // stays discoverable as a native hover tooltip so nothing was actually lost — the strip
      // just stopped echoing the current page above the hero that already names it.
      title={description ? `${label} — ${description}` : undefined}
      onMouseEnter={() => onHint?.(hint)}
      onMouseLeave={() => onHint?.(null)}
      onFocus={() => onHint?.(hint)}
      onBlur={() => onHint?.(null)}
    >
      {({ isActive }) => (
        <>
          <span className="nav-item-glyph" aria-hidden="true">
            {icon}
          </span>
          {/* The label and its description are SIBLINGS, not a nested pair: the tab bar and
              the header row lay this item out today and must not shift by a pixel. The
              description simply does not exist in those two contexts (display: none) and
              becomes the item's second grid row in the sheet, which is the surface with room
              to print it. */}
          <span className="nav-item-label" data-testid="nav-label" data-active={isActive ? "true" : "false"}>
            {label}
          </span>
          {description ? <span className="nav-item-desc">{description}</span> : null}
        </>
      )}
    </NavLink>
  );
}

/* ============================================================================ FIELD
   Label -> control gap --space-2. Help -> control gap --space-2. No exceptions. Help sits
   in a reserved slot so a column WITH help and a column WITHOUT still baseline together. */

export function Field({
  label,
  help,
  error,
  children
}: {
  label: string;
  help?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      <span>{label}</span>
      {children}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : help ? (
        <p className="field-help">{help}</p>
      ) : null}
    </div>
  );
}

/**
 * A single-line-semantics text control that WRAPS instead of truncating.
 *
 * An <input> can never display a value longer than its box, so the document title clipped to
 * "Wellcome Trust audi..." at every viewport. A document product may not hide the name of the
 * document. This keeps input semantics (Enter does not insert a newline, the value stays one
 * line) while letting the text wrap to as many lines as it needs. ui-breaker asserts that no
 * control clips, so the truncation cannot come back.
 */
export function AutoGrowInput({
  value,
  onValueChange,
  className,
  ...rest
}: {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "className">) {
  const ref = useRef<HTMLTextAreaElement>(null);

  function resize(element: HTMLTextAreaElement | null) {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }

  useEffect(() => {
    resize(ref.current);
  }, [value]);

  // The title/subtitle are set in Source Serif 4. If they first measured under the fallback
  // face they can land a line short once the packaged font swaps in (the same re-wrap that
  // clips the editor body). Re-measure the moment the fonts are ready. Guarded for non-browser.
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.ready) return;
    document.fonts.ready.then(() => resize(ref.current)).catch(() => {});
  }, []);

  return (
    <textarea
      {...rest}
      ref={ref}
      className={`auto-grow-input ${className || ""}`}
      rows={1}
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value.replace(/[\r\n]+/g, " "));
        resize(event.currentTarget);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }}
    />
  );
}

/**
 * SEGMENTED — survives for CHEAP, REVERSIBLE, NON-GOVERNED view toggles ONLY, where a bare
 * label is honest. Every governed choice (run mode, L0-L3, purpose) belongs in an OptionCard
 * that carries its consequence. A bare strip of four unlabelled buttons is not a surface a
 * professional can make a governed decision from.
 */
export function Segmented({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(([optionValue, optionLabel]) => (
        <button
          type="button"
          key={optionValue}
          className={value === optionValue ? "active" : ""}
          aria-pressed={value === optionValue}
          onClick={() => onChange(optionValue)}
        >
          {optionLabel}
        </button>
      ))}
    </div>
  );
}

/* ============================================================================ DATALIST
   LIVE STATE ONLY. One shared right edge, so every value lands on one column instead of
   each row negotiating its own.

   If a value CANNOT CHANGE, it may not go in a DataList. That is a trust rule, not a layout
   rule: the old StatusStack rendered "Status: needs review" (a real, live system state) and
   "Protected facts: Locked" (a static product promise) in the same two-column readout, which
   invites an operator to read marketing as a verified check. Static rules go in RuleList. */

export function DataList({ items, label }: { items: Array<[string, React.ReactNode]>; label?: string }) {
  return (
    <dl className="data-list" aria-label={label}>
      {items.map(([term, value]) => (
        <div className="data-row" key={term}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * RULELIST — static product rules. Deliberately NOT a two-column readout: a plain sentence
 * list, no right-aligned value, no pill, nothing that could be mistaken for a live check.
 */
export function RuleList({ items, label }: { items: Array<{ title: string; body: string }>; label?: string }) {
  return (
    <ul className="rule-list" aria-label={label}>
      {items.map((item) => (
        <li key={item.title}>
          <Lock aria-hidden="true" />
          <span>
            <strong>{item.title}</strong>
            <small>{item.body}</small>
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ============================================================================ STATUS PILL
   Verdict carried by GLYPH + TEXT + COLOUR. Never colour alone. The meta is exhaustive over
   RunState and every change verdict, with NO index fallback, so the BUILD FAILS if a state
   is unmapped — "blocked_for_human" can never silently degrade into looking benign. */

function StatusGlyphIcon({ glyph }: { glyph: StatusGlyph }) {
  if (glyph === "check") return <CheckCircle2 aria-hidden="true" />;
  if (glyph === "cross") return <XCircle aria-hidden="true" />;
  if (glyph === "alert") return <AlertTriangle aria-hidden="true" />;
  if (glyph === "clock") return <Clock aria-hidden="true" />;
  if (glyph === "spinner") return <Loader2 aria-hidden="true" className="spin" />;
  if (glyph === "lock") return <Lock aria-hidden="true" />;
  return <Circle aria-hidden="true" />;
}

export function StatusPill({ status }: { status: PillStatus }) {
  const meta = statusMeta(status);
  return (
    <span
      className={`status-pill tone-${meta.tone}`}
      data-testid="status-pill"
      data-status={status}
      data-tone={meta.tone}
      title={meta.meaning}
    >
      <StatusGlyphIcon glyph={meta.glyph} />
      {meta.label}
    </span>
  );
}

/* ============================================================================ EMPTY STATE
   Exactly {glyph, title, body, action}. No fake skeleton lines, no fake toolbar, no fake
   format chips, no "Workspace ready" kicker. An empty state that draws a pretend document is
   lying about the one thing the product is for. */

export function EmptyState({
  glyph,
  title,
  body,
  action,
  className
}: {
  glyph: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`empty-state ${className || ""}`}>
      <span className="empty-state-glyph" aria-hidden="true">
        {glyph}
      </span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action ? <div className="empty-state-action">{action}</div> : null}
    </div>
  );
}
